// The app shell around the web app (iOS / Android). Renders nothing in a browser.
//
//   splash + status bar   the splash goes once React is up; the status bar follows the theme
//   links into the app    day.busybee.app://oauth-return/... (a finished third-party sign-in,
//                         see links.js) and https://busybee.day/... universal / app links
//   Android back button   goes back, or sends the app to the background on the first page
//   offline               a full-screen notice until the connection is back (the app needs it)
//   version gate          an app older than the API's minimum asks to be updated
//   bots' bees            Android's notifications and both share sheets show each bot's bee (botFaces.js)
//   sharing into the app  the share sheet's targets and what was shared (intents.js, shareDraft.js)
//   keyboard images       an image the keyboard inserts is pasted where one writes (keyboardImages.js)
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Download, WifiOff } from 'lucide-react';
import { App as CapApp } from '@capacitor/app';
import { Preferences } from '@capacitor/preferences';
import { Network } from '@capacitor/network';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Button } from '../components/ui/button';
import { publicApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { closeAuthBrowser, openExternal, returnPathFromAppUrl } from './links';
import { WEB_URL, isAndroid, isIOS, isNative, platform } from './platform';
import { rememberLanguage, startPush } from './push';
import {
  filesFromShare, messageFromShare, saveShareBots, shareBots, shareDestination, takePending, taskFromShare,
} from './intents';
import { putShareDraft } from './shareDraft';
import { useTask } from '../contexts/TaskContext';
import { useAllChatsQuery, useMyBotsQuery } from '../hooks/queries';
import { botLabel } from '../components/bots/botDisplay';
import { newChatPath } from '../components/bots/chatLinks';
import { saveFaces } from './botFaces';
import { listenForKeyboardImages } from './keyboardImages';

const newer = (a, b) => {
  const pa = String(a).split('.').map(Number);
  const pb = String(b).split('.').map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i += 1) {
    if ((pa[i] || 0) !== (pb[i] || 0)) return (pa[i] || 0) > (pb[i] || 0);
  }
  return false;
};

// The same full-screen layout as NotFoundPage.
const FullScreen = ({ icon: Icon, title, text, action, onAction }) => (
  <div className="fixed inset-0 z-[300] bg-background flex items-center justify-center px-4">
    <div className="text-center max-w-md">
      <Icon className="w-10 h-10 text-amber-400 mx-auto mb-4" />
      <h2 className="text-2xl font-semibold text-foreground mb-2">{title}</h2>
      <p className="text-muted-foreground mb-8">{text}</p>
      {action && <Button onClick={onAction}>{action}</Button>}
    </div>
  </div>
);

function useStatusBarFollowsTheme() {
  useEffect(() => {
    const apply = () => {
      const dark = document.documentElement.classList.contains('dark');
      // Edge to edge on both systems: the page draws under the bar (viewport-fit=cover and
      // env(safe-area-inset-*), which Capacitor's SystemBars fills on Android too).
      StatusBar.setStyle({ style: dark ? Style.Dark : Style.Light }).catch(() => {});
    };
    apply();
    const observer = new MutationObserver(apply);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
}

function NativeShell() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, token, user } = useAuth();
  const { openTaskCreation } = useTask();
  const [online, setOnline] = useState(true);
  const [storeUrl, setStoreUrl] = useState(null); // set when this version is too old
  useStatusBarFollowsTheme();

  useEffect(() => {
    SplashScreen.hide().catch(() => {});

    const subs = [];
    subs.push(CapApp.addListener('appUrlOpen', async ({ url }) => {
      const back = returnPathFromAppUrl(url);
      if (back) {
        await closeAuthBrowser();
        navigate(back);
        return;
      }
      if (url.startsWith(WEB_URL)) {
        const { pathname, search, hash } = new URL(url);
        navigate(`${pathname}${search}${hash}`);
      }
    }));
    if (isAndroid()) {
      subs.push(CapApp.addListener('backButton', ({ canGoBack }) => {
        if (canGoBack) window.history.back();
        else CapApp.minimizeApp();
      }));
    }
    Network.getStatus().then((s) => setOnline(s.connected)).catch(() => {});
    subs.push(Network.addListener('networkStatusChange', (s) => setOnline(s.connected)));
    return () => subs.forEach((p) => p.then((h) => h.remove()));
  }, [navigate]);

  // Pushes once signed in: a tapped notification opens its page.
  useEffect(() => {
    if (isAuthenticated) startPush((url) => navigate(url)).catch(() => {});
  }, [isAuthenticated, navigate]);

  // What the phone asked for while the app was closed or in the background: a tapped
  // notification, an icon shortcut, something shared from another app (native/intents.js):
  // a new task with it, or a new chat with the bot it was shared to.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const handle = async () => {
      const { url, share } = await takePending();
      if (share) {
        const files = await filesFromShare(share.files);
        const to = shareDestination(share.target);
        if (to.kind === 'chat') {
          putShareDraft({ botId: to.botId, text: messageFromShare(share), files });
          navigate(newChatPath({ botId: to.botId }));
        } else {
          navigate('/app/tasks');
          openTaskCreation({ ...taskFromShare(share), files });
        }
      } else if (url) {
        const target = new URL(url, WEB_URL);
        const quickAdd = target.searchParams.get('quick_add');
        target.searchParams.delete('quick_add');
        navigate(`${target.pathname}${target.search}`);
        if (quickAdd) openTaskCreation();
      }
    };
    handle().catch(() => {});
    // iOS hands over an icon shortcut a moment after the app becomes active: look twice.
    const sub = CapApp.addListener('resume', () => {
      handle().catch(() => {});
      setTimeout(() => handle().catch(() => {}), 600);
    });
    return () => { sub.then((h) => h.remove()); };
  }, [isAuthenticated, navigate, openTaskCreation]);

  // Android draws the bots' bees beside their notifications (native/botFaces.js), and both
  // systems offer the bots of the latest chats in the share sheet (native/intents.js).
  const { data: mine } = useMyBotsQuery({ enabled: isAuthenticated });
  const { data: recent } = useAllChatsQuery({}, { enabled: isAuthenticated, refetchInterval: false });
  useEffect(() => {
    if (mine) saveFaces(mine).catch(() => {});  // iOS: the share sheet's suggestions and list
  }, [mine]);
  // iOS's share extension sends what is shared itself (it cannot open the app): it signs in as
  // the person through the App Group (ios/App/App/ShareBridge.swift), gone when they sign out.
  useEffect(() => {
    if (!isIOS()) return;
    const auth = isAuthenticated && token && user?.id
      ? JSON.stringify({ token, api: process.env.REACT_APP_BACKEND_URL, user_id: user.id }) : null;
    (auth ? Preferences.set({ key: 'bb_share_auth', value: auth }) : Preferences.remove({ key: 'bb_share_auth' }))
      .catch(() => {});
  }, [isAuthenticated, token, user?.id]);
  useEffect(() => {
    if (!mine) return;
    const bots = mine.bots || [];
    const recentIds = (recent?.pages?.[0]?.chats || []).map((c) => c.bot_id);
    saveShareBots(shareBots(bots, recentIds, (b) => botLabel(b, t, bots))).catch(() => {});
  }, [mine, recent, t]);

  // An image from the keyboard is pasted where the person is writing (keyboardImages.js).
  useEffect(() => (isAndroid() ? listenForKeyboardImages() : undefined), []);

  // Native code titles notification buttons and channels in the app's language.
  useEffect(() => { rememberLanguage(i18n.language).catch(() => {}); }, [i18n.language]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([CapApp.getInfo(), publicApi.mobileConfig()])
      .then(([info, res]) => {
        const p = platform();
        const min = res.data?.min_version?.[p];
        if (!cancelled && min && newer(min, info.version)) {
          setStoreUrl(res.data?.store_url?.[p] || WEB_URL);
        }
      })
      .catch(() => {}); // offline or an old API: never block on the check itself
    return () => { cancelled = true; };
  }, []);

  if (storeUrl) {
    return (
      <FullScreen icon={Download} title={t('native.update_title')} text={t('native.update_text')}
        action={t('native.update_button')} onAction={() => openExternal(storeUrl)} />
    );
  }
  if (!online) {
    return (
      <FullScreen icon={WifiOff} title={t('native.offline_title')} text={t('native.offline_text')}
        action={t('native.retry')}
        onAction={() => Network.getStatus().then((s) => setOnline(s.connected)).catch(() => {})} />
    );
  }
  return null;
}

export default function NativeBridge() {
  if (!isNative()) return null;
  return <NativeShell />;
}
